// src/app/features/orders/TaxExtensionOrderItem/index.tsx
import React from 'react';
import Button from 'Components/Buttons/Button';
import Tooltip from 'Components/Tooltips';
import UserBox from 'Components/UserBox';
import { USDate } from 'Components/Dates/USDate';
import {
  ITaxExtension,
  usePayTaxExtensionMutation,
} from 'app/services/taxExtensions';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'utils/AuthProvider';

interface Props {
  extension: ITaxExtension;
}

export default function TaxExtensionOrderItem({ extension }: Props) {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [payTaxExtension, { isLoading: paying }] = usePayTaxExtensionMutation();
  const price = extension.extension_price ?? 0;
  const isPaid = extension.payment_status === 'paid';
  const isCancelled = extension.status === 'cancelled';
  let displayStatus = 'unknown';
  if (isCancelled) {
    displayStatus = 'cancelled';
  } else if (isPaid) {
    displayStatus = 'completed';
  } else {
    displayStatus = 'pending payment';
  }

  // Only show the button if the current user is the extension creator.
  const canPay =
    !isPaid &&
    !isCancelled &&
    currentUser &&
    extension.creator.id === currentUser.id;

  async function handlePay() {
    try {
      const res = await payTaxExtension(String(extension.id)).unwrap();
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
      }
    } catch (err) {
      console.error('Failed to initiate Tax Extension payment', err);
      alert('Payment error, please try again or contact support.');
    }
  }

  return (
    <div className="w-2/5 md-max:w-full rounded-3xl shadow-all grid gap-2 pb-4">
      {/* -- header: user + status + button -- */}
      <div className="border-b px-4 py-2 text-gray-dark text-sm border-gray-100 grid gap-2">
        {extension.creator && (
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
            {/* Left side: user info */}
            <UserBox user={extension.creator} size="xs" theme="white" />

            {/* Right side: status label and button (if applicable) */}
            <div className="flex flex-col items-start md:items-end gap-2">
              <div
                className={`text-xs capitalize px-2 py-1 rounded-lg w-max font-bold ${
                  isPaid
                    ? 'text-green'
                    : isCancelled
                    ? 'text-red-dark'
                    : 'text-red-dark'
                }`}
              >
                {displayStatus}
              </div>
              {canPay && (
                <Button
                  theme="green-fill"
                  onClick={handlePay}
                  isLoading={paying}
                >
                  Pay Now
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Line item details */}
      <div className="border-b border-gray-100 px-4">
        <span className="text-sm text-gray-dark font-semibold">
          Tax Extension
        </span>
        <div className="text-sm text-gray-700 pl-1 pb-1">
          <span className="capitalize flex gap-4 items-center">
            <span className="text-gray-dark">
              {extension.title || `${extension.year} Tax Extension`}
            </span>
            <span className="ml-auto shadow rounded-md px-2 py-1">
              Extension
            </span>
            <span className="text-green text-regular font-bold inline-flex items-center">
              ${price}
            </span>
          </span>
        </div>
      </div>

      {/* Total */}
      <div className="flex px-4 text-base font-bold text-gray-700 justify-between">
        Total
        <span className="text-green">${price.toFixed(2)}</span>
      </div>

      {/* Meta info */}
      <div className="max-w-min">
        <Tooltip message="Tax Extension ID">
          <span className="px-4 text-xs whitespace-nowrap text-gray-700">
            <span className="select-all">{extension.id}</span>
          </span>
        </Tooltip>
      </div>
      <USDate
        className="text-sm text-gray-700 px-4"
        date={extension.created_at}
      />
    </div>
  );
}
